// src/pages/AllReferencesPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';

import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';

import { refItems } from '../data/SiteData';

const AllReferencesPage = () => {
  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Resources', path: '/resources' },
    { title: 'References', path: '/resources/references' },
  ];

  // Accordion expanded state
  const [expandedAccordions, setExpandedAccordions] = useState({
    date: false,
    type: false,
  });

  const handleAccordionToggle = (panel) => {
    setExpandedAccordions((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  // Date filter state
  const [showRange, setShowRange] = useState(false);
  const [afterChecked, setAfterChecked] = useState(false);
  const [beforeChecked, setBeforeChecked] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [yearAfter, setYearAfter] = useState(currentYear);
  const [yearBefore, setYearBefore] = useState(currentYear);
  const [monthAfter, setMonthAfter] = useState(currentMonth);
  const [monthBefore, setMonthBefore] = useState(currentMonth);

  // Sort
  const [sortOrder, setSortOrder] = useState('Latest');

  // TYPE options
  const typeOptions = ['Educational', 'Publication', 'Reference', 'Tool', 'Other Resource'];
  const initialTypeState = typeOptions.reduce((acc, t) => {
    acc[t.toLowerCase()] = true;
    return acc;
  }, {});
  const [typeChecked, setTypeChecked] = useState(initialTypeState);

  const handleTypeChange = (type) => {
    const key = type.toLowerCase();
    setTypeChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearAllTypes = () => {
    const cleared = typeOptions.reduce((acc, t) => ({ ...acc, [t.toLowerCase()]: false }), {});
    setTypeChecked(cleared);
  };

  const handleSelectAllTypes = () => {
  const allSelected = typeOptions.reduce(
    (acc, t) => ({ ...acc, [t.toLowerCase()]: true }),
    {}
  );
  setTypeChecked(allSelected);
};


  // UI helpers
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const inputHeight = 32;

  const enabledTextColor = '#142434';
  const disabledTextColor = '#999C9F';

  const CustomNumberStepper = ({ value, setValue, disabled, min, max }) => {
    const textColor = disabled ? disabledTextColor : enabledTextColor;
    return (
      <Box
        sx={{
          display: 'flex',
          border: '1px solid #ccc',
          overflow: 'hidden',
          width: '80px',
          height: `${inputHeight}px`,
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            color: textColor,
          }}
        >
          {value}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #ccc', width: '24px' }}>
          <IconButton
            size="small"
            onClick={() => !disabled && setValue(Math.min(max, value + 1))}
            sx={{ p: 0, minHeight: 0, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: `8px solid ${textColor}`,
              }}
            />
          </IconButton>
          <Divider sx={{ borderColor: '#ccc', m: 0 }} />
          <IconButton
            size="small"
            onClick={() => !disabled && setValue(Math.max(min, value - 1))}
            sx={{ p: 0, minHeight: 0, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `8px solid ${textColor}`,
              }}
            />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const CustomCheckboxIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#eee' : '#fff',
      }}
    />
  );

  const CustomCheckboxCheckedIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#ccc' : '#489596',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::after': {
          content: '""',
          width: 6,
          height: 10,
          border: 'solid #fff',
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        },
      }}
    />
  );

  const CustomRadioIcon = (
    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #000', backgroundColor: '#fff', display: 'inline-block' }} />
  );

  const CustomRadioCheckedIcon = (
    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #000', backgroundColor: '#489596', display: 'inline-block' }} />
  );

  // Filtered & sorted items
  const filteredItems = useMemo(() => {
    return refItems
      .filter((item) => {
        const t = (item.refType || '').toLowerCase().trim();
        return !!typeChecked[t];
      })
      .filter((item) => {
        if (!showRange) return true;
        const itemDate = new Date(item.date);
        const afterDate = new Date(yearAfter, monthAfter, 1);
        const beforeDate = new Date(yearBefore, monthBefore, 1);
        if (afterChecked && itemDate < afterDate) return false;
        if (beforeChecked && itemDate > beforeDate) return false;
        return true;
      })
      .sort((a, b) =>
        sortOrder === 'Latest' ? new Date(b.date) - new Date(a.date) : new Date(a.date) - new Date(b.date)
      );
  }, [
    typeChecked,
    showRange,
    afterChecked,
    beforeChecked,
    yearAfter,
    yearBefore,
    monthAfter,
    monthBefore,
    sortOrder,
  ]);

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="References"
        heroDescription="Explore published work, educational materials, and supporting resources."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto', display: 'flex', gap: '32px' }}>
        {/* LEFT COLUMN */}
        <Box sx={{ width: '248px', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', height: 'fit-content' }}>
          <Box sx={{ backgroundColor: 'rgba(72,149,150,0.1)', height: '40px', display: 'flex', alignItems: 'center', px: 2 }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 400, color: '#142434' }}>
              Refine Results
            </Typography>
          </Box>

          {/* DATE ACCORDION */}
          <Accordion
            expanded={expandedAccordions.date}
            onChange={() => handleAccordionToggle('date')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
            >
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                DATE
              </Typography>
            </AccordionSummary>

            <Divider />

            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4' }}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={showRange ? 'range' : 'all'}
                  onChange={(e) => {
                    const rangeSelected = e.target.value === 'range';
                    setShowRange(rangeSelected);
                    if (!rangeSelected) {
                      setAfterChecked(false);
                      setBeforeChecked(false);
                    }
                  }}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio icon={CustomRadioIcon} checkedIcon={CustomRadioCheckedIcon} />}
                    label="Show All"
                    sx={{ fontFamily: 'PT Sans, sans-serif' }}
                  />
                  <FormControlLabel
                    value="range"
                    control={<Radio icon={CustomRadioIcon} checkedIcon={CustomRadioCheckedIcon} />}
                    label="Range"
                    sx={{ fontFamily: 'PT Sans, sans-serif' }}
                  />
                </RadioGroup>
              </FormControl>

              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* AFTER */}
                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={afterChecked}
                        onChange={(e) => setAfterChecked(e.target.checked)}
                        disabled={!showRange}
                        icon={<CustomCheckboxIcon disabled={!showRange} />}
                        checkedIcon={<CustomCheckboxCheckedIcon disabled={!showRange} />}
                      />
                    }
                    label="After"
                    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px' }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, pr: 0 }}>
                    <Select
                      size="small"
                      value={monthAfter}
                      onChange={(e) => setMonthAfter(e.target.value)}
                      disabled={!afterChecked}
                      sx={{
                        width: '90px',
                        height: `${inputHeight}px`,
                        backgroundColor: '#fff',
                        color: !afterChecked ? disabledTextColor : enabledTextColor,
                      }}
                    >
                      {months.map((m, i) => (
                        <MenuItem key={m} value={i}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                    <CustomNumberStepper value={yearAfter} setValue={setYearAfter} disabled={!afterChecked} min={2000} max={new Date().getFullYear()} />
                  </Box>
                </Box>

                {/* BEFORE */}
                <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={beforeChecked}
                        onChange={(e) => setBeforeChecked(e.target.checked)}
                        disabled={!showRange}
                        icon={<CustomCheckboxIcon disabled={!showRange} />}
                        checkedIcon={<CustomCheckboxCheckedIcon disabled={!showRange} />}
                      />
                    }
                    label="Before"
                    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px' }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, pr: 0 }}>
                    <Select
                      size="small"
                      value={monthBefore}
                      onChange={(e) => setMonthBefore(e.target.value)}
                      disabled={!beforeChecked}
                      sx={{
                        width: '90px',
                        height: `${inputHeight}px`,
                        backgroundColor: '#fff',
                        color: !beforeChecked ? disabledTextColor : enabledTextColor,
                      }}
                    >
                      {months.map((m, i) => (
                        <MenuItem key={m} value={i}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                    <CustomNumberStepper value={yearBefore} setValue={setYearBefore} disabled={!beforeChecked} min={2000} max={new Date().getFullYear()} />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* TYPE ACCORDION */}
          <Accordion
            expanded={expandedAccordions.type}
            onChange={() => handleAccordionToggle('type')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
            >
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                TYPE
              </Typography>
            </AccordionSummary>

            <Divider />

            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {typeOptions.map((t) => (
                <FormControlLabel
                  key={t}
                  control={
                    <Checkbox
                      checked={typeChecked[t.toLowerCase()]}
                      onChange={() => handleTypeChange(t)}
                      icon={<CustomCheckboxIcon disabled={false} />}
                      checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                    />
                  }
                  label={t}
                  sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px' }}
                />
              ))}

              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
    <Typography
      onClick={handleClearAllTypes}
      sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}
    >
      Clear all
    </Typography>

    <Typography
      onClick={handleSelectAllTypes}
      sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}
    >
      Select all
    </Typography>
  </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: '#142434' }}>
              {filteredItems.length} Results
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: '#142434' }}>Sort:</Typography>
              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                sx={{ width: '100px', height: '32px', backgroundColor: '#fff' }}
              >
                <MenuItem value="Latest">Latest</MenuItem>
                <MenuItem value="Oldest">Oldest</MenuItem>
              </Select>
            </Box>
          </Box>

          {filteredItems.length === 0 && (
            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '20px', fontStyle: 'italic', color: '#999C9F', pl: 5, mb: 2 }}>
              No results have been found. Please edit your filters on the left.
            </Typography>
          )}

          {filteredItems.map((item) => {
            const isExternal = item.type === 'external';
            const linkTarget = isExternal ? item.extLink : item.link;

            return (
              <Card key={item.refId} sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, border: '1px solid #ddd', borderRadius: '8px', boxShadow: 'none', p: 2 }}>
                <Box
                  component="a"
                  href={linkTarget}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  sx={{ display: 'block', mr: 2 }}
                >
                  <Box component="img" src={item.image} alt={item.title} sx={{ width: '120px', height: 'auto', borderRadius: '4px', objectFit: 'cover' }} />
                </Box>

                <CardContent sx={{ padding: 0 }}>
                  <Typography
                    component={isExternal ? 'a' : RouterLink}
                    href={isExternal ? linkTarget : undefined}
                    to={!isExternal ? linkTarget : undefined}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#489596',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      mb: '4px',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {item.title}
                    {isExternal && <LaunchIcon sx={{ fontSize: '18px' }} />}
                  </Typography>

                  <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '14px', color: '#142434', mb: 1 }}>{item.date}</Typography>

                  <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '15px', color: '#142434', lineHeight: 1.4 }}>
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}

          <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', color: '#142434' }}>{filteredItems.length} Results</Typography>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default AllReferencesPage;
